import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  LinearProgress,
  Paper,
} from "@mui/material";
import { IconChevronLeft, IconCamera, IconBluetooth, IconFlask } from "@tabler/icons-react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { markingApi } from "../api";
import {
  bannerSx,
  barIconActiveSx,
  barIconSx,
  camera,
  cardSx,
  ds,
  dsText,
  scannerFieldSx,
  sectionLabelSx,
  shellSx,
  toneColor,
  topBarSx,
} from "../theme/keeperTheme";
import {
  MARKING_ZXING_HINTS,
  crptParseFailed,
  focusInputNoScroll,
  resolveGtinFromScan,
} from "../utils/markingScan";

const SCANNER_STRIP_H = 88;
const CAMERA_H = 200;
const DEBOUNCE_CAMERA_MS = 380;
const DEBOUNCE_SCANNER_MS = 90;

function formatResultSummary(result) {
  if (!result) return null;
  const lines = [];
  lines.push(`Сохранено: ${result.saved_path || "—"}`);
  if (result.crpt_data?.type) {
    lines.push(`Тип: ${result.crpt_data.type}`);
  }
  if (result.crpt_http_status != null) {
    lines.push(`ЧЗ HTTP: ${result.crpt_http_status}`);
  }
  if (result.crpt_error) {
    lines.push(`Ошибка ЧЗ: ${result.crpt_error}`);
  }
  const gtin = result.gtin_from_crpt || result.gtin_from_gs1;
  if (gtin) lines.push(`GTIN: ${gtin}`);
  if (result.crpt_parse_failed) {
    lines.push("ЧЗ не разобрал код");
  }
  return lines.join("\n");
}

export default function MarkingTestScanPage() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const scanControlsRef = useRef(null);
  const scannerInputRef = useRef(null);
  const checkTimerRef = useRef(null);
  const lastProcessedRef = useRef("");
  const inputModeRef = useRef("scanner");

  const [inputMode, setInputMode] = useState("scanner");
  inputModeRef.current = inputMode;

  const [cameraError, setCameraError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [scannedText, setScannedText] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [statusTone, setStatusTone] = useState("info");
  const [lastResult, setLastResult] = useState(null);
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    if (inputMode !== "camera") return undefined;
    let stream;
    let cancelled = false;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });
        if (cancelled) return;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setCameraReady(true);
        setCameraError(null);
      } catch (e) {
        setCameraError(e?.message || "Нет доступа к камере");
        setCameraReady(false);
      }
    })();
    return () => {
      cancelled = true;
      if (stream) stream.getTracks().forEach((t) => t.stop());
      setCameraReady(false);
    };
  }, [inputMode]);

  useEffect(() => {
    if (inputMode !== "scanner") return undefined;
    const t = window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 100);
    return () => window.clearTimeout(t);
  }, [inputMode]);

  useEffect(() => {
    if (inputMode !== "camera" || !cameraReady || cameraError) return undefined;
    const videoEl = videoRef.current;
    if (!videoEl) return undefined;
    let cancelled = false;
    const reader = new BrowserMultiFormatReader(MARKING_ZXING_HINTS);
    reader
      .decodeFromVideoElement(videoEl, (result) => {
        if (!cancelled && result) setScannedText(result.getText());
      })
      .then((controls) => {
        if (!cancelled) scanControlsRef.current = controls;
        else controls.stop();
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      scanControlsRef.current?.stop();
      scanControlsRef.current = null;
    };
  }, [inputMode, cameraReady, cameraError]);

  useEffect(() => {
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    const raw = scannedText.trim();
    if (!raw) {
      setLoading(false);
      return undefined;
    }

    const debounceMs = inputMode === "scanner" ? DEBOUNCE_SCANNER_MS : DEBOUNCE_CAMERA_MS;
    checkTimerRef.current = setTimeout(() => {
      checkTimerRef.current = null;
      if (lastProcessedRef.current === raw || loading) return;
      lastProcessedRef.current = raw;
      setLoading(true);
      setStatusMessage(null);
      (async () => {
        try {
          const result = await markingApi.testScan({ code: raw });
          setLastResult(result);
          setSavedCount((n) => n + 1);
          setStatusTone("success");
          setStatusMessage(formatResultSummary(result));
          if (inputModeRef.current === "scanner") {
            setScannedText("");
            if (scannerInputRef.current) scannerInputRef.current.value = "";
            window.setTimeout(() => focusInputNoScroll(scannerInputRef.current), 50);
          } else {
            setScannedText("");
          }
          lastProcessedRef.current = "";
        } catch (e) {
          setStatusTone("error");
          setStatusMessage(e?.message || "Ошибка сохранения теста");
          lastProcessedRef.current = "";
        } finally {
          setLoading(false);
        }
      })();
    }, debounceMs);

    return () => {
      if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    };
  }, [scannedText, inputMode, loading]);

  const toggleInputMode = () => {
    setInputMode((m) => (m === "camera" ? "scanner" : "camera"));
    setScannedText("");
    lastProcessedRef.current = "";
    if (scannerInputRef.current) scannerInputRef.current.value = "";
  };

  const handleScannerKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const v = (e.target.value || "").trim();
      e.target.value = "";
      if (v) setScannedText(v);
    }
  };

  const displayGtin = lastResult
    ? resolveGtinFromScan(lastResult.crpt_data, lastResult.raw_code || "")
    : null;
  const parseFailed = lastResult?.crpt_data ? crptParseFailed(lastResult.crpt_data) : false;

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 1,
          ...topBarSx,
        }}
      >
        <IconButton onClick={() => navigate("/")} sx={barIconSx} aria-label="Назад">
          <IconChevronLeft size={26} />
        </IconButton>
        <IconFlask size={22} style={{ color: dsText.warning }} />
        <Typography sx={{ color: ds.text, fontWeight: 700, fontSize: "1.0625rem" }}>
          Тест маркировки
        </Typography>
        {savedCount > 0 ? (
          <Typography sx={{ ml: "auto", color: ds.text2, fontSize: "0.8rem", pr: 1 }}>
            сохранено: {savedCount}
          </Typography>
        ) : null}
      </Box>

      <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, color: ds.text2, fontSize: "0.85rem" }}>
        Сканируйте QR / Data Matrix — ответ True API Честного знака (токен режима) сохранится на сервере для разбора.
      </Typography>

      <Box sx={{ px: 2, py: 1, display: "flex", gap: 1 }}>
        <IconButton
          onClick={toggleInputMode}
          sx={{
            ...(inputMode === "scanner" ? barIconActiveSx : barIconSx),
          }}
          aria-label="Сканер"
        >
          <IconBluetooth size={22} />
        </IconButton>
        <IconButton
          onClick={toggleInputMode}
          sx={{
            ...(inputMode === "camera" ? barIconActiveSx : barIconSx),
          }}
          aria-label="Камера"
        >
          <IconCamera size={22} />
        </IconButton>
      </Box>

      {inputMode === "camera" ? (
        <Box
          sx={{
            height: CAMERA_H,
            position: "relative",
            bgcolor: camera.bg,
            borderRadius: `${ds.radiusLg}px`,
            border: `1px solid ${ds.border2}`,
            overflow: "hidden",
            mx: 2,
          }}
        >
          {cameraError ? (
            <Typography sx={{ color: camera.errorText, p: 2, fontSize: "0.85rem" }}>{cameraError}</Typography>
          ) : (
            <video ref={videoRef} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          )}
        </Box>
      ) : (
        <Box sx={{ px: 2, height: SCANNER_STRIP_H, display: "flex", alignItems: "center" }}>
          <TextField
            inputRef={scannerInputRef}
            fullWidth
            size="small"
            placeholder="Сканируйте сюда…"
            onKeyDown={handleScannerKeyDown}
            autoComplete="off"
            sx={{
              "& .MuiOutlinedInput-root": {
                ...scannerFieldSx["& .MuiOutlinedInput-root"],
              },
            }}
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ px: 2, pt: 1 }}>
          <LinearProgress sx={{ "& .MuiLinearProgress-bar": { bgcolor: ds.primary } }} />
        </Box>
      ) : null}

      {statusMessage ? (
        <Paper
          elevation={0}
          sx={{
            ...bannerSx(statusTone === "success" ? "success" : "error"),
            mx: 2,
            mt: 1.5,
          }}
        >
          <Typography
            sx={{
              color: toneColor(statusTone === "success" ? "success" : "error"),
              fontSize: "0.82rem",
              fontWeight: 500,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {statusMessage}
          </Typography>
        </Paper>
      ) : null}

      {lastResult?.crpt_data ? (
        <Box sx={{ px: 2, pt: 1.5, flex: 1, minHeight: 0, overflow: "auto" }}>
          <Typography sx={{ ...sectionLabelSx, mb: 0.75, display: "block" }}>
            ПОСЛЕДНИЙ ОТВЕТ ЧЗ
          </Typography>
          <Paper
            elevation={0}
            sx={{
              ...cardSx,
              p: 1.5,
            }}
          >
            {displayGtin ? (
              <Typography sx={{ color: ds.primaryHover, fontSize: "0.85rem", fontWeight: 600, mb: 1 }}>
                GTIN: {displayGtin}
                {parseFailed ? " · ЧЗ: не разобрал" : ""}
              </Typography>
            ) : (
              <Typography sx={{ color: dsText.warning, fontSize: "0.85rem", fontWeight: 600, mb: 1 }}>
                GTIN не найден (ни в ЧЗ, ни в GS1)
              </Typography>
            )}
            <Typography
              component="pre"
              sx={{
                color: ds.text2,
                fontSize: "0.7rem",
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                maxHeight: 240,
                overflow: "auto",
              }}
            >
              {JSON.stringify(lastResult.crpt_data, null, 2)}
            </Typography>
          </Paper>
        </Box>
      ) : null}
    </Box>
  );
}
